<template>
    <div class="container-fluid  pt-2">
        <div v-if="supplierId">
            <div class="container pt-5">
                <div class="d-flex flex-wrap justify-content-between align-items-start">
                    <div class="mb-2">
                        <h1 class="h3 font-weight-bold text-capitalize text-primary mb-1">{{ selectedSupplier.supplier }}</h1>
                        <p class="text-muted mb-0">
                            <span v-if="selectedSupplier.phone">{{ selectedSupplier.phone }}</span>
                            <span v-if="selectedSupplier.phone && selectedSupplier.email"> · </span>
                            <span v-if="selectedSupplier.email">{{ selectedSupplier.email }}</span>
                        </p>
                    </div>
                    <div class="dropdown mb-2">
                        <a type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown" aria-expanded="false">Action</a>
                        <div class="dropdown-menu dropdown-menu-right">
                            <a class="dropdown-item" v-b-modal.modal-supplier-payment>Record Payment</a>
                            <a class="dropdown-item" :href="statementPageUrl">View Statement</a>
                            <a class="dropdown-item" href="#" @click.prevent="downloadStatement">Download Statement (PDF)</a>
                        </div>
                    </div>
                </div>

                <div class="form-row align-items-end mt-3">
                    <div class="col-md-6 col-12 mb-2">
                        <label class="mb-1">Period</label>
                        <date-range-picker :auto-apply="true" :append-to-body="true" v-model="dateRange" class="w-100">
                            <template #input="picker">
                                <span v-if="picker.startDate && picker.endDate">{{ formatPickerDate(picker.startDate) }} - {{ formatPickerDate(picker.endDate) }}</span>
                                <span v-else class="text-muted"><i class="bi bi-calendar-event mr-1"></i>All time</span>
                            </template>
                        </date-range-picker>
                    </div>
                    <div class="col-md-2 col-12 mb-2" v-if="filters.from || filters.to">
                        <button class="btn btn-outline-secondary btn-block" @click="clearPeriod">Clear</button>
                    </div>
                </div>

                <div class="row mt-3">
                    <div class="col-6 col-md mb-3">
                        <small class="text-muted text-uppercase">Total purchased</small>
                        <h4 class="mb-0">{{ convertCurrencyToLocal(report.totals?.total_spent || 0) }}</h4>
                    </div>
                    <div class="col-6 col-md mb-3">
                        <small class="text-muted text-uppercase">Paid</small>
                        <h4 class="mb-0">{{ convertCurrencyToLocal(report.totals?.total_paid || 0) }}</h4>
                    </div>
                    <div class="col-6 col-md mb-3">
                        <small class="text-muted text-uppercase">Balance owed</small>
                        <h4 class="mb-0" :class="(report.totals?.balance || 0) > 0 ? 'text-danger' : ''">{{ convertCurrencyToLocal(report.totals?.balance || 0) }}</h4>
                    </div>
                    <div class="col-6 col-md mb-3">
                        <small class="text-muted text-uppercase">Purchases</small>
                        <h4 class="mb-0">{{ report.totals?.purchase_count || 0 }}</h4>
                    </div>
                    <div class="col-6 col-md mb-3">
                        <small class="text-muted text-uppercase">Avg days between restocks</small>
                        <h4 class="mb-0">{{ report.totals?.average_days_between_restocks ?? '—' }}</h4>
                    </div>
                </div>

                <div v-if="loadingReport" class="text-center py-4"><span class="spinner-border text-primary"></span></div>
                <template v-else>
                    <h5 class="mt-3">Top items</h5>
                    <table class="table table-striped">
                        <thead>
                            <tr><th>Item</th><th class="text-right">Times restocked</th><th class="text-right">Quantity</th></tr>
                        </thead>
                        <tbody>
                            <tr v-for="(val, pid) in report.top_products" :key="pid">
                                <td>{{ val.product?.product ?? 'Deleted product' }}</td>
                                <td class="text-right">{{ val.count }}</td>
                                <td class="text-right">{{ val.qty }}</td>
                            </tr>
                            <tr v-if="!report.top_products || !Object.keys(report.top_products).length">
                                <td colspan="3" class="text-center text-muted">No restocks in this period.</td>
                            </tr>
                        </tbody>
                    </table>

                    <h5 class="mt-4">Purchases</h5>
                    <table class="table table-striped">
                        <thead>
                            <tr><th>Purchase order</th><th>Date</th><th class="text-right">Amount</th><th class="text-right">Paid</th></tr>
                        </thead>
                        <tbody>
                            <tr v-for="p in report.purchases" :key="p.id">
                                <td>{{ p.purchase_order }}</td>
                                <td>{{ FormatDate(p.created_at) }}</td>
                                <td class="text-right">{{ convertCurrencyToLocal(p.total_amount) }}</td>
                                <td class="text-right">{{ convertCurrencyToLocal(p.amount_paid || 0) }}</td>
                            </tr>
                            <tr v-if="!report.purchases || !report.purchases.length">
                                <td colspan="4" class="text-center text-muted">No purchases in this period.</td>
                            </tr>
                        </tbody>
                    </table>
                </template>

                <div v-if="selectedSupplier && selectedSupplier.supplier_reps && selectedSupplier.supplier_reps.length" class="mt-4 mb-5">
                    <h5>Representative</h5>
                    <p class="mb-1" v-if="selectedSupplier.supplier_reps[0].name">Name: {{ selectedSupplier.supplier_reps[0].name }}</p>
                    <p class="mb-1" v-if="selectedSupplier.supplier_reps[0].phone">Phone: {{ selectedSupplier.supplier_reps[0].phone }}</p>
                    <p class="mb-1" v-if="selectedSupplier.supplier_reps[0].email">Email: {{ selectedSupplier.supplier_reps[0].email }}</p>
                </div>
            </div>
        </div>
        <div v-else class="pt-5">
            <div class="d-flex flex-wrap justify-content-between align-items-center">
                <h4 class="font-weight-bold text-primary mb-2">Suppliers</h4>
                <div class="mb-2">
                    <a v-b-modal="'new-supplier-modal'" class="btn btn-primary">Create Supplier <i class="bi bi-plus"></i></a>
                    <a @click="downloadSuppliersFile" class="btn btn-outline-secondary">Download List <i class="bi bi-download"></i></a>
                </div>
            </div>

            <div class="form-row align-items-end pt-3">
                <div class="col-md-5 col-12 mb-2">
                    <label class="mb-1">Search</label>
                    <input v-model="listSearch" type="text" class="form-control" placeholder="Supplier name, phone or email">
                </div>
                <div class="col-md-5 col-12 mb-2">
                    <label class="mb-1">Period</label>
                    <date-range-picker :auto-apply="true" :append-to-body="true" v-model="dateRange" class="w-100">
                        <template #input="picker">
                            <span v-if="picker.startDate && picker.endDate">{{ formatPickerDate(picker.startDate) }} - {{ formatPickerDate(picker.endDate) }}</span>
                            <span v-else class="text-muted"><i class="bi bi-calendar-event mr-1"></i>All time</span>
                        </template>
                    </date-range-picker>
                </div>
                <div class="col-md-2 col-12 mb-2" v-if="filters.from || filters.to">
                    <button class="btn btn-outline-secondary btn-block" @click="clearPeriod">Clear</button>
                </div>
            </div>

            <div class="row mt-3">
                <div class="col-6 col-md-3 mb-3">
                    <small class="text-muted text-uppercase">Suppliers</small>
                    <h4 class="mb-0">{{ suppliersTable.items.length }}</h4>
                </div>
                <div class="col-6 col-md-3 mb-3">
                    <small class="text-muted text-uppercase">Total purchased</small>
                    <h4 class="mb-0">{{ convertCurrencyToLocal(listTotals.spent) }}</h4>
                </div>
                <div class="col-6 col-md-3 mb-3">
                    <small class="text-muted text-uppercase">Paid</small>
                    <h4 class="mb-0">{{ convertCurrencyToLocal(listTotals.paid) }}</h4>
                </div>
                <div class="col-6 col-md-3 mb-3">
                    <small class="text-muted text-uppercase">Balance owed</small>
                    <h4 class="mb-0" :class="listTotals.balance > 0 ? 'text-danger' : ''">{{ convertCurrencyToLocal(listTotals.balance) }}</h4>
                </div>
            </div>

            <div style="margin-bottom: 5em;">
                <div v-if="loadingList" class="text-center py-4"><span class="spinner-border text-primary"></span></div>
                <b-table v-else striped hover sticky-header="100vh" :items="suppliersTable.items" :fields="suppliersTable.fields"
                    :filter="listSearch" :filter-included-fields="['name', 'phone', 'email']"
                    sort-icon-left responsive="sm" @row-clicked="supplierRowSelected" show-empty empty-text="No suppliers yet." empty-filtered-text="No suppliers match your search.">
                    <template #cell(index)="data">
                        <span>{{ data.index + 1 }}</span>
                    </template>
                    <template #cell(name)="data">
                        <span class="text-primary">{{ data.item.name }}</span>
                        <div class="text-muted small" v-if="data.item.phone || data.item.email">{{ [data.item.phone, data.item.email].filter(Boolean).join(' · ') }}</div>
                    </template>
                    <template #cell(total_spent)="data">{{ convertCurrencyToLocal(data.item.total_spent) }}</template>
                    <template #cell(total_paid)="data">{{ convertCurrencyToLocal(data.item.total_paid) }}</template>
                    <template #cell(balance)="data">
                        <span :class="data.item.balance > 0 ? 'text-danger' : ''">{{ convertCurrencyToLocal(data.item.balance) }}</span>
                    </template>
                    <template #cell(last_purchase_at)="data">{{ data.item.last_purchase_at ? FormatDate(data.item.last_purchase_at) : '—' }}</template>
                </b-table>
            </div>
            <b-modal ref="newSupplierModal" id="new-supplier-modal" hide-footer static>

                <div class="container text-center">
                    <div class="text-centre">
                        <h2 class="font-weight-bold ">Add New Supplier</h2>
                    </div>

                </div>

                <div class="container py-3">
                    <!-- Actual search box -->
                    <form @submit.prevent="createNewSupplier">
                        <p>Company details</p>
                        <div class="form-group">
                            <input class="form-control border-bottom" v-model="supplierDetails.supplier.supplier"
                                type="text" placeholder="Company Name" required />

                        </div>

                        <div class="form-row py-1">
                            <div class="col-6">
                                <input class="form-control border-bottom" v-model="supplierDetails.supplier.email"
                                    type="email" placeholder="Email" />
                            </div>
                            <div class="col-6">
                                <input class="form-control border-bottom" v-model="supplierDetails.supplier.phone"
                                    type="text" placeholder="Phone Number" />

                            </div>


                        </div>
                        <hr />
                        <p>Company representative details</p>
                        <div class="form-row py-1">
                            <div class="col-6">
                                <input class="form-control border-bottom"
                                    v-model="supplierDetails.supplier.supplier_representatives.name" type="text"
                                    placeholder="Representative " />
                            </div>
                            <div class="col-6">
                                <input class="form-control border-bottom"
                                    v-model="supplierDetails.supplier.supplier_representatives.phone" type="text"
                                    placeholder="Phone" />

                            </div>


                        </div>

                        <div class="form-group pt-3">
                            <button class="btn btn-primary btn-block">Add Supplier</button>
                        </div>
                    </form>
                </div>



            </b-modal>

        </div>

        <!-- MODALS -->

         <b-modal  id="modal-supplier-payment" hide-footer hide-header static>

           <supplier-payment-recording>
</supplier-payment-recording>
        </b-modal>
    </div>
</template>

<script>
import SupplierPaymentRecording from '../modal/SupplierPaymentRecording.vue';
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';
import { downloadPdf, downloadFile } from '../../services/pdfDownload';

    export default {
        name: 'suppliers',

        components: {SupplierPaymentRecording, DateRangePicker},
        data() {
            return {
                suppliersTable: {
                    fields: [
                        "index",
                        { key: "name", sortable: true },
                        { key: "purchases_count", label: "Purchases", sortable: true, class: "text-right" },
                        { key: "total_spent", label: "Purchased", sortable: true, class: "text-right" },
                        { key: "total_paid", label: "Paid", sortable: true, class: "text-right" },
                        { key: "balance", label: "Balance", sortable: true, class: "text-right" },
                        { key: "last_purchase_at", label: "Last purchase", sortable: true },
                    ],
                    items: [],
                },
                filters: { from: '', to: '' },
                listSearch: '',
                loadingList: false,
                loadingReport: false,
                orderstable: {
                    fields: [
                        "index",
                        {
                            key: "order",
                            sortable: true
                        },
                        {
                            key: "amount",
                            sortable: true
                        },
                        {
                            key: "date_",
                            sortable: true
                        },
                    ],
                    items: []
                },
                smsForm: {
                    message: ''
                },

                supplierDetails: {
                    supplier: {
                        email: '',
                        phone: '',
                        supplier: '',
                        supplier_representatives: {
                            name: '',
                            email: '',
                            phone: ''
                        },
                    },
                    searchTerm: '',
                    searchTable: false,
                    filteredSuppliers: {},

                },
                selectedSupplier: {},
                supplier: null, report: {}

            }
        },
        computed: {
            supplierId() {
            const params = new URLSearchParams(window.location.search);
            return params.get('id');
            },
            dateRange: {
                get() {
                    return {
                        startDate: this.filters.from ? new Date(this.filters.from) : null,
                        endDate: this.filters.to ? new Date(this.filters.to) : null,
                    };
                },
                set(val) {
                    this.filters.from = val && val.startDate ? moment(val.startDate).format('YYYY-MM-DD') : '';
                    this.filters.to = val && val.endDate ? moment(val.endDate).format('YYYY-MM-DD') : '';
                    this.reload();
                },
            },
            periodParams() {
                const params = {};
                if (this.filters.from) params.from = this.filters.from;
                if (this.filters.to) params.to = this.filters.to;
                return params;
            },
            statementPageUrl() {
                const params = new URLSearchParams({ id: this.supplierId, ...this.periodParams });
                return `/supplier-statement?${params.toString()}`;
            },
            listTotals() {
                return this.suppliersTable.items.reduce((t, s) => ({
                    spent: t.spent + (s.total_spent || 0),
                    paid: t.paid + (s.total_paid || 0),
                    balance: t.balance + (s.balance || 0),
                }), { spent: 0, paid: 0, balance: 0 });
            },
        },
        mounted() {

            (this.supplierId) ? this.fetchSuppliers(this.supplierId) : this.getSuppliers()
        },
        methods: {
            customerTrxRowSelected(item) {
                window.location.replace('/orders?order-number=' + item.id + '&source=/suppliers' )
            },
            getSuppliers() {
                this.loadingList = true;
                axios.get('/api/reports/suppliers', { params: this.periodParams })
                    .then(result => {
                        this.suppliersTable.items = (result.data || []).map(s => ({
                            supplier_id: s.id,
                            name: s.name,
                            phone: s.phone,
                            email: s.email,
                            purchases_count: s.purchases_count,
                            total_spent: s.total_spent,
                            total_paid: s.total_paid,
                            balance: s.balance,
                            last_purchase_at: s.last_purchase_at,
                        }));
                    })
                    .finally(() => { this.loadingList = false; });
            },
            async fetchSuppliers(id) {
                this.loadingReport = true;
                try {
                    const { data } = await axios.get(`/api/reports/suppliers/${id}`, { params: this.periodParams });
                    this.selectedSupplier = data.supplier;
                    this.supplier = data.supplier;
                    this.report = data;
                } finally {
                    this.loadingReport = false;
                }
            },
            reload() {
                this.supplierId ? this.fetchSuppliers(this.supplierId) : this.getSuppliers();
            },
            clearPeriod() {
                this.filters.from = '';
                this.filters.to = '';
                this.reload();
            },
            formatPickerDate(d) {
                return d ? moment(d).format('D MMM YYYY') : '';
            },
            async downloadStatement() {
                const params = new URLSearchParams({ supplier_id: this.supplierId, ...this.periodParams });
                const name = (this.selectedSupplier.supplier || 'supplier').replace(/[^a-z0-9]+/gi, '-');
                try {
                    await downloadPdf(`/supplier-statement/pdf?${params.toString()}`, `Statement-${name}.pdf`);
                } catch (e) {
                    this.$toasts.push({ type: 'error', message: 'Could not download the statement. Please try again.' });
                }
            },
            supplierRowSelected(items) {
                window.location.href=`/suppliers?id=${items.supplier_id}`

            },
            sendSMSToCustomer() {
                axios.post('/api/send-sms-to-customer', {
                    customer: this.customer_data,
                    message: this.smsForm.message
                }).then(results => {
                    if (results.delivery === 'success') {
                        this.$toasts.push({
                            'type': "success",
                            "message": "Message sent successfully"
                        })
                    }
                    this.$refs.textmessage_modal.hide()
                })
            },

            submitSearchCustomer() {
                this.getCustomers();
            },
            FormatDate(date) {
                let finaldate = moment(date).format('DD-MMM-YYYY');
                return finaldate;
            },
            DeleteCustomer(customer_id) {
                axios.get('/api/customer_delete', {
                        params: {
                            customer_id
                        }
                    })
                    .then(result => {
                        this.$root.$toasts.push({
                            type: 'success',
                            message: "Customer data has been deleted",
                        });
                        this.$refs['customermodaldel'].hide();
                        this.getCustomers();
                    });
            },
            async createNewSupplier() {
                await axios.post('/api/new-supplier', { ...this.supplierDetails.supplier
                }).then(results => {
                    if (results.status === 200) {
                        this.supplierDetails.supplier = results.data.supplier
                        this.supplierDetails.supplier.supplier_representatives = results.data.rep.supplier_representatives
                        this.$refs.newSupplierModal.hide()
                    }
                }).catch(errors => {
                })
            },
            EditCustomerInfo(e) {
                e.preventDefault();
                axios.post('/api/updatecustomer', {
                        customer_id: this.selected_customer,
                        town: this.town,
                        street: this.street,
                        apartment: this.apartment,
                        h_no: this.h_no,
                        phone_no: this.phone_no,
                        email: this.c_email,
                        l_name: this.l_name,
                        f_name: this.f_name
                    })
                    .then(result => {
                        if (result.status === 200) {
                            this.$root.$toasts.push({
                                type: 'success',
                                message: "Customer data updated successfully",
                            });
                            this.$refs['edit-customer-modal'].hide();
                            this.getCustomers()
                        } else {
                            this.$root.$toasts.push({
                                type: 'error',
                                message: "There was an error adding customer",
                            });
                        }
                    })
                    .catch(error => {
                        this.$root.$toasts.push({ type: 'error', message: error.response?.data?.message || 'Could not update the customer.' });
                    });
            },
            EditCustomerModal() {
                this.$refs['edit-customer-modal'].show();
                this.$refs['customer_profile'].hide();
            },
            downloadSuppliersFile() {
                const params = new URLSearchParams({ export: 1, ...this.periodParams });
                downloadFile(`/api/all-suppliers?${params.toString()}`, 'suppliers.xlsx')
                    .catch(() => this.$toasts.push({ type: 'error', message: 'Could not download the list. Please try again.' }));
            }
        }
    }
</script>
